import { SUPPORTED_LANGUAGES, SupportedLanguage } from "@/config/config";
import { useNavigate, useRouterState, NavigateOptions } from "@tanstack/react-router";
import { useCallback } from "react";

// --- 1. Remove '/$lang' prefix if it exists ---
type RemoveLangPrefix<T extends string> = T extends `/$lang${infer Rest}`
  ? Rest extends ""
    ? "/"
    : Rest extends `/${string}`
      ? Rest
      : `/${Rest}`
  : T;

// --- 2. I18nNavigateOptions keeps NavigateOptions but transforms `to` type ---
type I18nNavigateOptions = NavigateOptions extends infer O
  ? O extends { to?: infer To }
    ? Omit<O, "to"> & {
        to?: To extends string ? RemoveLangPrefix<To> : undefined;
      }
    : never
  : never;

/**
 * Hook for i18n-aware navigation
 * Automatically includes the current language prefix when navigating
 * Preserves all TanStack Router NavigateOptions except overrides 'to' to accept string paths
 *
 * @example
 * ```tsx
 * const navigate = useI18nNavigate();
 *
 * // I18n navigation with full TanStack Router options
 * navigate({
 *   to: "/programs",
 *   search: { category: "food" },
 *   replace: true,
 *   state: { fromPage: "home" }
 * });
 * ```
 */
export const useI18nNavigate = () => {
  const navigate = useNavigate();

  const i18nNavigate = useCallback(
    (options: I18nNavigateOptions) => {
      // Work on a local string for runtime
      let to = options.to as string | undefined;
      if (to) {
        // --- safely extract current lang prefix ---
        const match = /^\/([a-z]{2})(?:\/|$)/.exec(location.pathname);
        const currentLang = match?.[1]; // e.g. 'en', 'ar' or undefined

        // --- prefix normalization ---
        if (currentLang && !to.startsWith(`/${currentLang}`)) {
          // Avoid double slashes
          to = to === "/" ? `/${currentLang}` : `/${currentLang}${to}`;
        }
      }

      /**
       * TS: navigate expects NavigateOptions["to"] (a narrow union of literal routes),
       * but `to` is a runtime string. We assert it back to NavigateOptions['to'].
       *
       * This cast is safe in practice because:
       * - callers passed a valid route literal (typed by NavigateOptions['to'])
       * - we only add a deterministic prefix string (e.g. `/ar`) to that literal
       *
       * If you want to avoid this cast entirely, you'd need to encode all possible
       * prefixed route literals into the type system (more complex).
       */
      navigate({
        // keep all other options (replace, from, search, etc.)
        ...(options as Omit<NavigateOptions, "to">),
        // assert the runtime string to the expected narrower type
        to: to as unknown as NavigateOptions["to"],
      } as NavigateOptions);
    },
    [navigate],
  );

  return i18nNavigate;
};

/**
 * Get the current language from the route
 */
export function useCurrentLanguage(): SupportedLanguage {
  const routerState = useRouterState();
  const currentLang = routerState.location.pathname.split("/")[1] as SupportedLanguage;

  return SUPPORTED_LANGUAGES.includes(currentLang) ? currentLang : "en";
}

/**
 * Generate a URL with the current language prefix
 */
export function useI18nUrl() {
  const currentLang = useCurrentLanguage();

  const generateUrl = useCallback(
    (path: string, params?: Record<string, any>) => {
      // Ensure path starts with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;

      // Replace any /$lang placeholder with actual language
      let finalPath = normalizedPath.replace("/$lang", `/${currentLang}`);

      // If path doesn't already include language, add it
      if (!finalPath.startsWith(`/${currentLang}`)) {
        finalPath = `/${currentLang}${finalPath}`;
      }

      // Handle params substitution if needed
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          finalPath = finalPath.replace(`:${key}`, String(value));
        });
      }

      return finalPath;
    },
    [currentLang],
  );

  return generateUrl;
}
